import React from 'react';
import { useTranslation } from 'react-i18next';
import { useChat } from '../../context/ChatContext';
import { logout } from '../../services/authService';
import { useNavigate } from 'react-router-dom';
import { LanguageSwitcher } from '../layout/LanguageSwitcher';
import BotSelector from './BotSelector';
import './ChatSidebar.css';

interface ChatSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Sidebar component displaying chat sessions
 * Shows bot selector (when multiple bots), session list, new chat button, logout, and language switcher
 */
function ChatSidebar({ isOpen, onClose }: ChatSidebarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    sessions,
    currentSessionId,
    switchSession,
    createNewSession,
    isStreaming,
    bots,
    botsLoading,
    currentBotId,
    setCurrentBotId,
  } = useChat();

  const showBotSelector = bots.length > 1;

  const handleNewChat = () => {
    if (isStreaming) return;
    createNewSession();
    onClose();
  };

  const handleSelectSession = async (sessionId: string) => {
    if (isStreaming) return;
    await switchSession(sessionId);
    onClose();
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div className="sidebar-overlay" onClick={onClose} role="presentation" />
      )}

      {/* Sidebar */}
      <aside className={`chat-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h2>{t('chat.sessions')}</h2>
          {showBotSelector && (
            <BotSelector
              bots={bots}
              selectedBotId={currentBotId}
              onBotSelect={setCurrentBotId}
              loading={botsLoading}
              disabled={isStreaming}
            />
          )}
          <button className="btn-new-chat" onClick={handleNewChat} type="button" disabled={bots.length === 0 || isStreaming}>
            + {t('chat.new_session')}
          </button>
        </div>

        <div className="sidebar-content">
          {sessions.length === 0 ? (
            <p className="no-sessions">{t('chat.no_sessions')}</p>
          ) : (
            <ul className="session-list">
              {sessions.map((session) => (
                <li
                  key={session.sessionId}
                  className={`session-item ${
                    session.sessionId === currentSessionId ? 'active' : ''
                  }${isStreaming && session.sessionId !== currentSessionId ? ' disabled' : ''}`}
                  onClick={() => handleSelectSession(session.sessionId)}
                  role="button"
                  tabIndex={isStreaming ? -1 : 0}
                  aria-disabled={isStreaming && session.sessionId !== currentSessionId}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelectSession(session.sessionId);
                    }
                  }}
                >
                  <div className="session-title">{session.title}</div>
                  <div className="session-meta">
                    <span className="message-count">
                      {session.messageCount} {session.messageCount === 1 ? 'message' : 'messages'}
                    </span>
                    <span className="last-message">
                      {new Date(session.lastMessageAt).toLocaleDateString()}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="sidebar-footer">
          <div className="sidebar-footer-actions">
            <LanguageSwitcher />
            <button className="btn-logout" onClick={handleLogout} type="button">
              {t('auth.sign_out')}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}

export default ChatSidebar;
